import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeading from '@/components/base/PageHeading';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { plotEpisodes } from '@/mocks/plotEpisodes';
import { episodeNo, formatDate } from '@/utils/format';

const PAGE_SIZE = 6;

export default function PlotCategory() {
  const [ascending, setAscending] = useState(true);
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: '分集剧情介绍 - 禁止的爱善良的小姨子电视剧专题',
    description:
      '禁止的爱善良的小姨子全十二集分集剧情介绍，按更新时间排序查看每集梗概、播出日期与时长，快速回顾顾家的故事脉络。',
    keywords: '禁止的爱善良的小姨子分集剧情,剧情介绍,剧集列表,电视剧专题',
  });

  const sorted = useMemo(() => {
    const list = [...plotEpisodes].sort((a, b) => a.episode - b.episode);
    return ascending ? list : list.reverse();
  }, [ascending]);

  const shown = sorted.slice(0, visible);

  return (
    <SiteLayout>
      <PageHeading
        crumbs={[{ label: '剧情' }]}
        eyebrow="PLOT ARCHIVE"
        title="分集剧情档案"
        description="从第一场雨到最后一次开门，十二集故事逐集整理。每集附剧情梗概、播出日期与时长，方便回看与对照细节。"
        count={`${plotEpisodes.length} 集 已收录`}
      />

      <section className="bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span className="flex h-4 w-4 items-center justify-center text-base text-primary-500">
                <i className="ri-sort-asc" />
              </span>
              共 {plotEpisodes.length} 集 · 当前按分集时间{ascending ? '升序' : '降序'}
            </div>
            <div className="flex items-center gap-1 self-start rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => {
                  setAscending(true);
                  setVisible(PAGE_SIZE);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                  ascending ? 'bg-primary-600 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                时间升序
              </button>
              <button
                type="button"
                onClick={() => {
                  setAscending(false);
                  setVisible(PAGE_SIZE);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                  !ascending ? 'bg-primary-600 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                时间降序
              </button>
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="mt-8">
              <EmptyState
                title="暂无相关剧情介绍"
                description="内容正在整理中，可以先回到首页查看专题概览，或浏览演员资料。"
              />
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((item, index) => (
                  <Link
                    key={item.id}
                    to={`/detail/plot/${item.id}`}
                    className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 animate-fade-up"
                    style={{ animationDelay: `${Math.min(index, 6) * 40}ms` }}
                  >
                    <div className="relative h-[180px] w-full overflow-hidden bg-background-200">
                      <img
                        src={item.image}
                        alt={`${episodeNo(item.episode)} ${item.title} 剧照`}
                        title={`${item.title} - 禁止的爱善良的小姨子分集剧情`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                      <span className="absolute left-3 top-3 rounded-sm bg-primary-600 px-2 py-1 text-[11px] text-background-50">
                        {episodeNo(item.episode)}
                      </span>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between text-[11px] text-foreground-400">
                        <span>{formatDate(item.date)}</span>
                        <span>{item.duration}</span>
                      </div>
                      <h2 className="mt-2 font-heading text-base text-foreground-900 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h2>
                      <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500">{item.summary}</p>
                      <span className="mt-4 flex items-center gap-1 text-xs text-accent-700">
                        查看剧情详情
                        <span className="flex h-3.5 w-3.5 items-center justify-center transition-transform group-hover:translate-x-0.5">
                          <i className="ri-arrow-right-line" />
                        </span>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                {visible < sorted.length ? (
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => Math.min(prev + PAGE_SIZE, sorted.length))}
                    className="whitespace-nowrap rounded-md border border-foreground-300 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-600"
                  >
                    加载更多剧情
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已展示全部 {sorted.length} 集剧情</p>
                )}
                <p className="text-[11px] text-foreground-400">
                  提示：分页为前端演示状态，后续接入内容后台后可由真实数据驱动
                </p>
              </div>
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}